'use client';

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

export function WhyHisea() {
  const { isMobile } = useIsMobile();

  const carouselRef = useRef<any>(null);

  const [isScrollingPaused, setIsScrollingPaused] = useState(false);

  const [isAnimating, setIsAnimating] = React.useState(false);

  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const pauseScrolling = () => {
    setIsScrollingPaused(true);
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
  };

  const resumeScrolling = () => {
    setIsScrollingPaused(false);
    window.removeEventListener('wheel', preventScroll);
    window.removeEventListener('touchmove', preventScroll);
  };

  const preventScroll = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const handleScroll = (event: any) => {
      console.log('handleScroll');
      const targetDiv = document.getElementById('why-hisea-block');
      if (!targetDiv) return;

      const divTop = targetDiv.getBoundingClientRect().top;

      if (divTop <= 180 && !isScrollingPaused && !isAnimating) {
        // 假设距离视口顶部 100px 时触发
        // 假设距离顶部 100px 时触发
        startAnimation();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: false });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isAnimating]);

  const startAnimation = React.useCallback(() => {
    if (!carouselRef.current) {
      return;
    }
    pauseScrolling();

    setIsAnimating(true);
    setTimeout(() => {
      carouselRef.current!.scrollNext();
      setTimeout(() => {
        carouselRef.current!.scrollNext();
        resumeScrolling();
      }, 1500);
    }, 1500);
  }, [carouselRef]);

  return (
    <div
      id='why-hisea-block'
      className='bg-[#f9f9f9] pt-[40px] lg_py-[80px]'
    >
      <div className='container mx-auto '>
        <div className=' px-[12px] lg_px-0'>
          <p className=' text-medium mb-[10px] font-teko-medium text-[28px] uppercase leading-[28px] lg_text-[40px] lg_leading-[40px]'>
            Why Hisea?
          </p>
          <p className='mb-[20px] font-roboto-regular text-[16px] leading-[24px] lg_mb-[40px] lg_text-[18px] lg_leading-[30px]'>
            Our boots are more than just footwear—they are tools for exploration. Each pair is crafted with advanced
            waterproof technology, breathable fabrics, and superior traction, ensuring you are prepared for any
            challenge. Here's what sets us apart:
          </p>
        </div>
        <div className='mb-[20px] flex w-full flex-col items-start px-[12px] lg_mb-[40px] lg_border-b-[1px] lg_border-[#E0E0E0] lg_px-[0]'>
          <div className=' flex flex-wrap items-center justify-start gap-x-[10px] gap-y-[20px] lg_gap-x-[40px] lg_gap-y-[0]'>
            {[
              {
                title: 'Waterproof Performance',
                index: 0,
              },
              {
                title: 'Multifunctional',
                index: 1,
              },
              {
                title: 'All-Day Comfort',
                index: 2,
              },
            ].map(item =>
              isMobile ? (
                <p
                  key={item.index}
                  className={cn(
                    'text-medium h-[26px] cursor-pointer rounded-full border-[1px] border-[#E0E0E0] px-[10px] font-roboto-medium text-[14px] leading-[26px] text-[#929292]',
                    item.index + 1 === current && 'border-none bg-black text-white ',
                  )}
                  onClick={() => {
                    api?.scrollTo(item.index);
                  }}
                >
                  {item.title}
                </p>
              ) : (
                <p
                  key={item.index}
                  className={cn(
                    'text-medium h-[40px] cursor-pointer font-roboto-medium leading-[40px]',
                    item.index + 1 === current && 'border-b-[3px] border-[#000] ',
                  )}
                  onClick={() => {
                    api?.scrollTo(item.index);
                  }}
                >
                  {item.title}
                </p>
              ),
            )}
          </div>
        </div>
        <div className='relative'>
          <Carousel
            setApi={setApi}
            ref={carouselRef}
            opts={{
              align: 'start',
            }}
            orientation={isMobile ? 'horizontal' : 'vertical'}
            className=' w-full'
          >
            <CarouselContent className='-ml-0 -mt-1 h-fit w-full lg_h-[650px]'>
              {Array.from({
                length: 3,
              }).map((_, index) => (
                <CarouselItem
                  key={index}
                  className='pl-0 pt-1 md_basis-full'
                >
                  <Image
                    src={
                      isMobile
                        ? `/about-us/why-hisea-${index + 1}-m.webp?v=1.2.3`
                        : `/about-us/why-hisea-${index + 1}.webp?v=1.2.3`
                    }
                    alt={`Why Hisea ${index + 1}`}
                    width={1440}
                    height={650}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* <div className='bottom-0 left-[-30px] mt-[10px] flex flex-row items-center justify-center gap-[10px] lg_absolute lg_flex-col'>
            {Array.from({
              length: count,
            }).map((_, index) => (
              <div
                key={index}
                className={cn('h-[10px] w-[10px] rounded-full bg-[#8EA0AA]', current === index + 1 && 'bg-[#DA6950]')}
              />
            ))}
          </div> */}
        </div>
      </div>
    </div>
  );
}
